import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GeneratedReferenceSign } from 'gen/api';
import { IPendingReferenceSignDelete, IPendingReferenceSignUpdate } from 'types/referenceSign';

interface IReferenceSignsState {
  figureIdsLoadingSigns: string[];
  pendingReferenceSignAdditions: GeneratedReferenceSign[];
  pendingReferenceSignUpdates: IPendingReferenceSignUpdate[];
  pendingReferenceSignDeletes: IPendingReferenceSignDelete[];
}

const initialState: IReferenceSignsState = {
  figureIdsLoadingSigns: [],
  pendingReferenceSignAdditions: [],
  pendingReferenceSignUpdates: [],
  pendingReferenceSignDeletes: [],
};

const referenceSignsSectionsSlice = createSlice({
  name: 'referenceSignsSections',
  initialState,
  reducers: {
    addFigureIdsLoadingSigns: (state, action: PayloadAction<string[]>) => {
      state.figureIdsLoadingSigns = [...state.figureIdsLoadingSigns, ...action.payload];
    },
    removeFigureIdsLoadingSigns: (state, action: PayloadAction<string[]>) => {
      state.figureIdsLoadingSigns = [...state.figureIdsLoadingSigns].filter(id => !action.payload.includes(id));
    },
    clearPendingReferenceSignAdditionByLabel: (state, action: PayloadAction<{ label: string; figureId: string }>) => {
      state.pendingReferenceSignAdditions = [...state.pendingReferenceSignAdditions]
        .map(sign => {
          if (sign.label === action.payload.label) {
            return { ...sign, figureIds: sign.figureIds.filter(id => id !== action.payload.figureId) };
          }
          return sign;
        })
        .filter(sign => sign.figureIds.length > 0);
    },
    clearPendingReferenceSignAdditionsForFigureId: (state, action: PayloadAction<string>) => {
      state.pendingReferenceSignAdditions = [...state.pendingReferenceSignAdditions]
        .map(sign => ({ ...sign, figureIds: sign.figureIds.filter(id => id !== action.payload) }))
        .filter(sign => sign.figureIds.length > 0);
    },
    addPendingReferenceSignAdditions: (state, action: PayloadAction<GeneratedReferenceSign[]>) => {
      const updated = [...state.pendingReferenceSignAdditions];
      for (const newSign of action.payload) {
        const existing = updated.find(s => s.label === newSign.label);
        if (existing) {
          // Same label already exists, merge figureIds so it spans both figures
          const mergedFigureIds = [...new Set([...existing.figureIds, ...newSign.figureIds])];
          existing.figureIds = mergedFigureIds;
        } else {
          updated.push(newSign);
        }
      }
      state.pendingReferenceSignAdditions = updated;
    },
    addPendingReferenceSignUpdates: (state, action: PayloadAction<IPendingReferenceSignUpdate[]>) => {
      const byId = new Map(state.pendingReferenceSignUpdates.map(u => [u.referenceSignId, u]));
      for (const incoming of action.payload) {
        byId.set(incoming.referenceSignId, incoming);
        // mutual exclusion: drop any pending delete for the same sign
        state.pendingReferenceSignDeletes = state.pendingReferenceSignDeletes.filter(
          d => d.referenceSignId !== incoming.referenceSignId,
        );
      }
      state.pendingReferenceSignUpdates = Array.from(byId.values());
    },
    removePendingReferenceSignUpdate: (state, action: PayloadAction<string>) => {
      state.pendingReferenceSignUpdates = state.pendingReferenceSignUpdates.filter(
        u => u.referenceSignId !== action.payload,
      );
    },
    addPendingReferenceSignDeletes: (state, action: PayloadAction<IPendingReferenceSignDelete[]>) => {
      const byId = new Map(state.pendingReferenceSignDeletes.map(d => [d.referenceSignId, d]));
      for (const incoming of action.payload) {
        byId.set(incoming.referenceSignId, incoming);
        // mutual exclusion: drop any pending update for the same sign
        state.pendingReferenceSignUpdates = state.pendingReferenceSignUpdates.filter(
          u => u.referenceSignId !== incoming.referenceSignId,
        );
      }
      state.pendingReferenceSignDeletes = Array.from(byId.values());
    },
    removePendingReferenceSignDelete: (state, action: PayloadAction<string>) => {
      state.pendingReferenceSignDeletes = state.pendingReferenceSignDeletes.filter(
        d => d.referenceSignId !== action.payload,
      );
    },
    clearPendingReferenceSignChanges: state => {
      state.pendingReferenceSignUpdates = [];
      state.pendingReferenceSignDeletes = [];
    },
  },
});

export default referenceSignsSectionsSlice.reducer;

export const {
  addFigureIdsLoadingSigns,
  removeFigureIdsLoadingSigns,
  addPendingReferenceSignAdditions,
  clearPendingReferenceSignAdditionByLabel,
  clearPendingReferenceSignAdditionsForFigureId,
  addPendingReferenceSignUpdates,
  removePendingReferenceSignUpdate,
  addPendingReferenceSignDeletes,
  removePendingReferenceSignDelete,
  clearPendingReferenceSignChanges,
} = referenceSignsSectionsSlice.actions;
